import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Gift, Smartphone } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import type { Listing, Status } from "@/types";
import { Heart, PageHeader, Star } from "@/components/Doodles";
import { ListingCard } from "@/components/ListingCard";
import { BriefLink } from "@/components/BriefLink";

const COUNTS: { key: Status; label: string }[] = [
  { key: "building", label: "Building" },
  { key: "in_review", label: "To review" },
  { key: "draft", label: "Needs work" },
  { key: "approved", label: "Approved" },
  { key: "sent", label: "On Etsy (draft)" },
];

export default function DashboardPage() {
  const [listings, setListings] = useState<Listing[] | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    api.get("/listings").then((r) => setListings(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const building = listings?.some((l) => l.status === "building");
  useEffect(() => {
    if (!building) { return; }
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [building, load]);

  const createPack = async () => {
    setCreating(true);
    try {
      await api.post("/packs/christmas");
      toast.success("Building 8 Christmas listings. This takes a few minutes.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const hasPack = listings?.some((l) => l.pack === "christmas");
  const toReview = listings?.filter((l) => l.status === "in_review").length || 0;

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Listings" kicker="Product engine">
        <Link to="/admin/review" className="btn-navy" data-testid="open-review-btn">
          <Smartphone className="h-4 w-4" strokeWidth={2.5} /> Review queue ({toReview})
        </Link>
      </PageHeader>
      <BriefLink />

      <section className="relative mb-10 overflow-hidden rounded-[28px] border-2 border-[#23324A] bg-[#F6D776] p-7 shadow-[6px_6px_0_#23324A] sm:p-10 fade-up" data-testid="christmas-pack-banner">
        <Star className="absolute right-6 top-6 hidden h-14 w-14 rotate-12 sm:block" color="#FFFFFF" />
        <Heart className="absolute bottom-6 right-28 hidden h-10 w-10 -rotate-12 sm:block" />
        <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-[#23324A]/70">Christmas upload · 8 listings, mostly bundles</p>
        <h2 className="max-w-xl font-display text-2xl font-black leading-tight text-[#23324A] sm:text-3xl">Little hands, big gifts. Get the Christmas pack ready for your 20-minute upload.</h2>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          {!hasPack ? (
            <button onClick={createPack} disabled={creating} className="btn-coral" data-testid="build-christmas-pack-btn">
              <Gift className="h-4 w-4" strokeWidth={2.5} /> {creating ? "Starting…" : "Build Christmas pack"}
            </button>
          ) : (
            <span className="rounded-full border-2 border-[#23324A] bg-white px-4 py-2 text-sm font-bold text-[#23324A]" data-testid="christmas-pack-status">
              {building ? "Building listings… this page refreshes itself" : "Christmas pack built. Review it on your phone."}
            </span>
          )}
        </div>
      </section>

      <div className="mb-8 grid grid-cols-2 gap-3 sm:grid-cols-5">
        {COUNTS.map((c) => (
          <div key={c.key} className="rounded-2xl border-2 border-[#23324A]/15 bg-white px-4 py-3" data-testid={`count-${c.key}`}>
            <p className="font-display text-2xl font-black text-[#23324A]">{listings?.filter((l) => l.status === c.key).length ?? "–"}</p>
            <p className="text-xs font-semibold text-[#23324A]/60">{c.label}</p>
          </div>
        ))}
      </div>

      {listings && listings.length === 0 && (
        <p className="text-sm text-[#23324A]/70" data-testid="no-listings">No listings yet. Build the Christmas pack, or try templates in the Studio.</p>
      )}
      <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3" data-testid="listings-grid">
        {listings?.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
      </div>
    </div>
  );
}
